/* Carousel Index — full-screen search.
   This is Carousel's own surface, so it wears the brand: the purple→pink
   gradient and neon accents. Org and team tiles inside it keep their stored
   flat colours. */

.overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  background: var(--nexus-bg);
  color: var(--nexus-text);
  font-family: var(--nexus-font-sans);
  font-size: var(--nexus-text-sm);
  overflow: hidden;
}

/* The brand glow at the top of the screen — what makes it a place of its own
   rather than a box floating over the page. */
.aura {
  position: absolute;
  z-index: -1;
  inset: 0 0 auto;
  height: 520px;
  pointer-events: none;
  background:
    radial-gradient(ellipse 55% 100% at 30% 0%, color-mix(in srgb, var(--nexus-purple) 40%, transparent), transparent 72%),
    radial-gradient(ellipse 45% 90% at 78% 0%, color-mix(in srgb, var(--nexus-pink) 26%, transparent), transparent 70%);
}

.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* --- Header ---------------------------------------------------------------- */

.header {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--nexus-space-lg);
  height: 64px;
  padding: 0 var(--nexus-space-2xl);
  border-bottom: 1px solid transparent;
  border-image: linear-gradient(
      90deg,
      color-mix(in srgb, var(--nexus-purple) 60%, transparent),
      color-mix(in srgb, var(--nexus-pink) 60%, transparent) 50%,
      transparent
    )
    1;
}

.brand {
  display: flex;
  align-items: center;
  gap: var(--nexus-space-md);
  min-width: 0;
}

.logo {
  flex: none;
  width: 28px;
  height: 28px;
}

.wordmark {
  font-family: var(--nexus-font-display);
  font-variation-settings: var(--nexus-display-settings);
  font-size: var(--nexus-text-2xl);
  line-height: 1;
  text-transform: uppercase;
  white-space: nowrap;
}

.gradientText,
.match {
  background: var(--nexus-gradient-brand-text);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.tagline {
  color: var(--nexus-text-muted);
  font-size: var(--nexus-text-sm);
  white-space: nowrap;
}

.close {
  display: inline-flex;
  align-items: center;
  gap: var(--nexus-space-sm);
  min-height: 44px;
  padding: 0 var(--nexus-space-md) 0 var(--nexus-space-lg);
  border: 1px solid var(--nexus-border);
  border-radius: var(--nexus-radius-control);
  background: var(--nexus-surface);
  color: var(--nexus-text);
  font: inherit;
  font-weight: var(--nexus-weight-medium);
  cursor: pointer;
  transition: border-color var(--nexus-transition-fast);
}

.close:hover {
  border-color: var(--nexus-border-strong);
}

.close:focus-visible,
.tab:focus-visible,
.row:focus-visible,
.topHit:focus-visible,
.seeAll:focus-visible,
.recent:focus-visible,
.primaryBtn:focus-visible,
.secondaryBtn:focus-visible {
  outline: 2px solid var(--nexus-border-focus);
  outline-offset: 2px;
}

.kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border: 1px solid var(--nexus-border-strong);
  border-bottom-width: 2px;
  border-radius: var(--nexus-radius-sm);
  background: var(--nexus-surface);
  color: var(--nexus-text-secondary);
  font: var(--nexus-weight-semibold) var(--nexus-text-xs) / 1 var(--nexus-font-sans);
}

/* --- Search box and tabs --------------------------------------------------- */

.searchArea {
  flex: none;
  width: min(1120px, 100% - 2 * var(--nexus-space-2xl));
  margin: 0 auto;
  padding-top: var(--nexus-space-2xl);
}

.inputWrap {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--nexus-space-lg);
  height: 64px;
  padding: 0 var(--nexus-space-xl);
  border: 2px solid transparent;
  border-radius: var(--nexus-radius-surface);
  background:
    linear-gradient(var(--nexus-surface), var(--nexus-surface)) padding-box,
    var(--nexus-gradient-brand-text) border-box;
  box-shadow: var(--nexus-glow-brand-strong);
}

.inputIcon {
  flex: none;
  color: var(--nexus-text-secondary);
}

.input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--nexus-text);
  font: var(--nexus-weight-normal) var(--nexus-text-xl) var(--nexus-font-sans);
  caret-color: var(--nexus-pink);
}

.input::placeholder {
  color: var(--nexus-text-muted);
}

.searching {
  flex: none;
  color: var(--nexus-text-muted);
  font-size: var(--nexus-text-xs);
}

.tabs {
  display: flex;
  gap: var(--nexus-space-xl);
  margin-top: var(--nexus-space-sm);
  border-bottom: 1px solid var(--nexus-border-subtle);
  overflow-x: auto;
  scrollbar-width: none;
}

.tab {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: var(--nexus-space-xs);
  min-height: 44px;
  padding: 0 2px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: var(--nexus-text-secondary);
  font: var(--nexus-weight-medium) var(--nexus-text-sm) var(--nexus-font-sans);
  cursor: pointer;
}

.tab:hover {
  color: var(--nexus-text);
}

.tab[aria-pressed='true'] {
  color: var(--nexus-text);
  border-image: var(--nexus-gradient-brand-text) 1;
}

.tab[data-empty] {
  color: var(--nexus-text-muted);
  opacity: 0.6;
}

.tabCount {
  color: var(--nexus-text-muted);
  font-size: var(--nexus-text-xs);
}

.tab[aria-pressed='true'] .tabCount {
  background: var(--nexus-gradient-brand-text);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: var(--nexus-weight-bold);
}

/* --- Body ------------------------------------------------------------------ */

.body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  width: min(1120px, 100% - 2 * var(--nexus-space-2xl));
  margin: 0 auto;
  padding: var(--nexus-space-xl) 0 var(--nexus-space-3xl);
}

.stack,
.group {
  display: flex;
  flex-direction: column;
  gap: var(--nexus-space-sm);
  min-width: 0;
}

.stack {
  gap: var(--nexus-space-xl);
}

.group {
  gap: var(--nexus-space-2xs);
}

.heading {
  margin: 0 0 var(--nexus-space-xs);
  color: var(--nexus-text-muted);
  font-size: var(--nexus-text-xs);
  font-weight: var(--nexus-weight-semibold);
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.headingCount {
  color: var(--nexus-text-muted);
  opacity: 0.7;
}

.sectionHead {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--nexus-space-sm);
}

.seeAll {
  padding: var(--nexus-space-xs);
  border: 0;
  background: transparent;
  font: var(--nexus-weight-semibold) var(--nexus-text-xs) var(--nexus-font-sans);
  cursor: pointer;
  background-image: var(--nexus-gradient-brand-text);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.panel {
  display: flex;
  flex-direction: column;
  padding: var(--nexus-space-xs);
  border: 1px solid var(--nexus-border);
  border-radius: var(--nexus-radius-surface);
  background: color-mix(in srgb, var(--nexus-surface) 85%, transparent);
}

.empty {
  padding: var(--nexus-space-4xl) 0;
  text-align: center;
}

.emptyTitle {
  margin: 0 0 var(--nexus-space-xs);
  font-size: var(--nexus-text-lg);
  font-weight: var(--nexus-weight-semibold);
}

.more {
  margin: var(--nexus-space-sm) var(--nexus-space-md) 0;
  color: var(--nexus-text-muted);
  font-size: var(--nexus-text-xs);
}

/* --- Rows ------------------------------------------------------------------ */

.row {
  display: flex;
  align-items: center;
  gap: var(--nexus-space-md);
  min-height: 52px;
  padding: var(--nexus-space-sm) var(--nexus-space-md);
  border: 1px solid transparent;
  border-radius: var(--nexus-radius-surface);
  color: var(--nexus-text);
  text-decoration: none;
  min-width: 0;
}

.row:hover {
  background: var(--nexus-surface);
}

.row[data-active] {
  border-color: transparent;
  background:
    linear-gradient(var(--nexus-surface), var(--nexus-surface)) padding-box,
    var(--nexus-gradient-brand-text) border-box;
}

.rowCompact {
  min-height: 44px;
  padding: var(--nexus-space-xs) var(--nexus-space-sm);
}

.rowText {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.rowName {
  font-size: var(--nexus-text-sm);
  font-weight: var(--nexus-weight-semibold);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rowSub {
  color: var(--nexus-text-secondary);
  font-size: var(--nexus-text-xs);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.match {
  font-weight: inherit;
}

.kindLabel {
  flex: none;
  padding: 3px 6px;
  border: 1px solid var(--nexus-border);
  border-radius: var(--nexus-radius-xs);
  background: var(--nexus-surface);
  color: var(--nexus-text-secondary);
  font-size: 10px;
  font-weight: var(--nexus-weight-bold);
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

/* --- Tiles ----------------------------------------------------------------- */

.tile {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--nexus-radius-media);
  background: var(--nexus-surface-hover);
  color: var(--nexus-text-on-primary);
  font-family: var(--nexus-font-display);
  font-variation-settings: var(--nexus-display-settings);
  object-fit: cover;
}

.tileDarkText {
  color: var(--nexus-text-inverse);
}

.tileRound {
  border-radius: var(--nexus-radius-full);
}

.tile_sm {
  width: 28px;
  height: 28px;
  font-size: var(--nexus-text-sm);
}

.tile_md {
  width: 36px;
  height: 36px;
  font-size: var(--nexus-text-lg);
}

.tile_lg {
  width: 56px;
  height: 56px;
  font-size: var(--nexus-text-3xl);
}

/* --- Competition status ---------------------------------------------------- */

.status {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  padding: 3px 7px;
  border-radius: var(--nexus-radius-xs);
  font-size: 11px;
  font-weight: var(--nexus-weight-bold);
  letter-spacing: 0.03em;
  white-space: nowrap;
}

.status_live {
  color: var(--nexus-pink);
  background: color-mix(in srgb, var(--nexus-pink) 14%, transparent);
}

.status_active {
  color: var(--nexus-purple);
  background: color-mix(in srgb, var(--nexus-purple) 16%, transparent);
}

.status_open {
  color: var(--nexus-green);
  background: color-mix(in srgb, var(--nexus-green) 12%, transparent);
}

.status_muted {
  color: var(--nexus-text-secondary);
  background: var(--nexus-surface-hover);
}

.statusText_live,
.statusText_active,
.statusText_open,
.statusText_muted {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-weight: var(--nexus-weight-semibold);
}

.statusText_live {
  color: var(--nexus-pink);
}

.statusText_active {
  color: var(--nexus-purple);
}

.statusText_open {
  color: var(--nexus-green);
}

.statusText_muted {
  color: var(--nexus-text-secondary);
}

.liveDot {
  width: 7px;
  height: 7px;
  border-radius: var(--nexus-radius-full);
  background: var(--nexus-pink);
  box-shadow: 0 0 10px var(--nexus-pink);
}

/* --- Top hit --------------------------------------------------------------- */

.topHit {
  display: flex;
  align-items: center;
  gap: var(--nexus-space-lg);
  padding: var(--nexus-space-lg) var(--nexus-space-xl);
  border: 1px solid transparent;
  border-radius: var(--nexus-radius-surface);
  background:
    linear-gradient(var(--nexus-surface), var(--nexus-surface)) padding-box,
    var(--nexus-gradient-brand-text) border-box;
  box-shadow: var(--nexus-glow-brand-strong);
  color: var(--nexus-text);
  text-decoration: none;
}

.topHit[data-active] {
  box-shadow: var(--nexus-glow-brand-strong), 0 0 0 3px color-mix(in srgb, var(--nexus-pink) 22%, transparent);
}

.topHitText {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--nexus-space-xs);
}

.topHitName {
  font-family: var(--nexus-font-display);
  font-variation-settings: var(--nexus-display-settings);
  font-size: var(--nexus-text-3xl);
  line-height: 1;
  text-transform: uppercase;
  overflow-wrap: anywhere;
}

/* --- Layouts --------------------------------------------------------------- */

.home,
.split {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: var(--nexus-space-3xl);
  align-items: start;
}

.split {
  grid-template-columns: minmax(0, 1fr) 460px;
  gap: var(--nexus-space-2xl);
}

.mixed {
  display: flex;
  flex-direction: column;
  gap: var(--nexus-space-xl);
}

.mixedTop {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--nexus-space-xl);
}

/* The top hit fills the height of the blended list beside it. */
.mixedTop .topHit {
  flex: 1;
}

.shelves {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--nexus-space-lg);
}

.shelf {
  display: flex;
  flex-direction: column;
  gap: var(--nexus-space-2xs);
  min-width: 0;
  padding: var(--nexus-space-md) var(--nexus-space-sm) var(--nexus-space-sm);
  border: 1px solid var(--nexus-border-subtle);
  border-radius: var(--nexus-radius-surface);
  background: color-mix(in srgb, var(--nexus-surface) 80%, transparent);
}

.shelf .sectionHead {
  padding: 0 var(--nexus-space-sm);
}

.recent {
  display: flex;
  align-items: center;
  gap: var(--nexus-space-md);
  min-height: 40px;
  padding: 0 var(--nexus-space-md);
  border: 0;
  border-radius: var(--nexus-radius-surface);
  background: transparent;
  color: var(--nexus-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.recent svg {
  color: var(--nexus-text-muted);
}

.recent:hover {
  background: var(--nexus-surface);
}

/* --- Preview --------------------------------------------------------------- */

.preview {
  position: sticky;
  top: 0;
  padding-top: 24px;
}

.previewTab {
  position: absolute;
  top: 0;
  left: 0;
  display: inline-flex;
  align-items: center;
  gap: var(--nexus-space-xs);
  height: 24px;
  padding: 0 var(--nexus-space-md);
  border-radius: var(--nexus-radius-sm) var(--nexus-radius-sm) 0 0;
  background: var(--nexus-gradient-brand-fill);
  color: var(--nexus-text-on-primary);
  font-size: 11px;
  font-weight: var(--nexus-weight-bold);
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.previewCard {
  overflow: hidden;
  border: 1px solid var(--nexus-border);
  border-radius: 0 var(--nexus-radius-surface) var(--nexus-radius-surface);
  background: var(--nexus-surface);
}

.previewBand {
  height: 4px;
  background: var(--nexus-border-strong);
}

.previewBody {
  display: flex;
  flex-direction: column;
  gap: var(--nexus-space-lg);
  padding: var(--nexus-space-xl);
}

.previewHead {
  display: flex;
  align-items: center;
  gap: var(--nexus-space-lg);
  min-width: 0;
}

.previewTitle {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--nexus-space-xs);
}

.previewName {
  font-family: var(--nexus-font-display);
  font-variation-settings: var(--nexus-display-settings);
  font-size: var(--nexus-text-3xl);
  line-height: 1;
  text-transform: uppercase;
  overflow-wrap: anywhere;
}

.previewNote {
  margin: 0;
  color: var(--nexus-text-secondary);
  font-size: var(--nexus-text-sm);
}

.previewTrust {
  display: flex;
  align-items: center;
  gap: var(--nexus-space-md);
}

.facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin: 0;
  border: 1px solid var(--nexus-border);
  border-radius: var(--nexus-radius-surface);
}

.fact {
  padding: var(--nexus-space-md) var(--nexus-space-lg);
  border-bottom: 1px solid var(--nexus-border);
}

.fact:nth-child(odd) {
  border-right: 1px solid var(--nexus-border);
}

/* An odd one out takes the whole row rather than leaving an empty cell. */
.fact:last-child:nth-child(odd) {
  grid-column: 1 / -1;
  border-right: 0;
}

.fact:nth-last-child(-n + 2):nth-child(odd),
.fact:last-child {
  border-bottom: 0;
}

.fact dt {
  color: var(--nexus-text-muted);
  font-size: var(--nexus-text-xs);
}

.fact dd {
  margin: 2px 0 0;
  font-weight: var(--nexus-weight-semibold);
}

.previewActions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--nexus-space-sm);
}

.primaryBtn,
.secondaryBtn {
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  padding: 0 var(--nexus-space-lg);
  border-radius: var(--nexus-radius-control);
  font-weight: var(--nexus-weight-semibold);
  text-decoration: none;
}

.primaryBtn {
  background: var(--nexus-gradient-brand-fill);
  color: var(--nexus-text-on-primary);
  box-shadow: 0 4px 20px color-mix(in srgb, var(--nexus-pink-active) 25%, transparent);
}

.secondaryBtn {
  border: 1px solid var(--nexus-border-strong);
  color: var(--nexus-text);
}

/* --- Footer ---------------------------------------------------------------- */

.footer {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--nexus-space-2xl);
  height: 48px;
  border-top: 1px solid var(--nexus-border-subtle);
  color: var(--nexus-text-muted);
  font-size: var(--nexus-text-xs);
}

.footer span {
  display: inline-flex;
  align-items: center;
  gap: var(--nexus-space-xs);
}

/* --- Phone ----------------------------------------------------------------- */

@media (max-width: 768px) {
  .header {
    height: 56px;
    padding: 0 var(--nexus-space-lg);
  }

  .tagline,
  .closeText,
  .close .kbd,
  .footer {
    display: none;
  }

  .close {
    width: 44px;
    justify-content: center;
    padding: 0;
  }

  .searchArea,
  .body {
    width: calc(100% - 2 * var(--nexus-space-lg));
  }

  .searchArea {
    padding-top: var(--nexus-space-lg);
  }

  .inputWrap {
    height: 52px;
    padding: 0 var(--nexus-space-lg);
  }

  .input {
    font-size: var(--nexus-text-base);
  }

  .home,
  .split,
  .mixedTop {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--nexus-space-xl);
  }

  /* A phone has no room beside the list; the result itself is one tap away. */
  .split .preview {
    display: none;
  }

  .topHitName {
    font-size: var(--nexus-text-2xl);
  }

  .topHit {
    padding: var(--nexus-space-lg);
  }

  .topHit .kindLabel {
    display: none;
  }
}
